import {
  FEE_LIFECYCLE_STAGE,
  FEE_PARTY_ROLE,
  FEE_RECURRENCE,
  FEE_UNIT_BASIS,
  FEE_VALUE_BASES_BY_VALUE_TYPE,
  FEE_VALUE_BASIS,
  FEE_VALUE_TYPE,
  requiresUnitBasis,
} from '@shared/constants/fees'
import { dateToIso } from '@shared/utils/dateTimeUtils'
import type {
  CreateFeeDefinitionPayload,
  SupersedeFeeDefinitionPayload,
} from '../api/feesMasterContracts'
import type { FeeDefinition, FeeDefinitionFormValues } from '../types/feeTypes'

const MAX_PERCENTAGE = 100
const MAX_CHANGE_REASON_LENGTH = 500

// Mirrors the API's own rule (`create-fees-master.request.dto.ts`): upper snake
// case, starting with a letter, 60 characters at most.
const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/
const MAX_CODE_LENGTH = 60

export type FeeFormErrors = Partial<Record<keyof FeeDefinitionFormValues, string>>

/**
 * A code already in force, with the market it is in force for.
 *
 * Both halves are needed: the API's uniqueness is on `(market_id, code)` over
 * the versions still open, so one code may exist once platform-wide and once
 * per market.
 */
export interface FeeCodeInForce {
  code: string
  marketId: string | null
}

/**
 * The code an admin gets for free from the fee's name: upper snake case, which
 * is the form every code in `fees_master` takes.
 *
 * Anything that is not a letter or a digit separates words, so "PMC fee (annual)"
 * and "PMC fee - annual" both come out `PMC_FEE_ANNUAL`. A leading digit is
 * dropped rather than kept, because the API requires the first character to be a
 * letter and a code the admin cannot save is worse than a shorter one.
 */
export function feeCodeFromName(name: string): string {
  return name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^[^A-Z]+/, '')
    .replace(/_+$/, '')
    .slice(0, MAX_CODE_LENGTH)
}

/**
 * Keeps a hand-typed code in the shape the API accepts without fighting the
 * typist: separators become underscores as they are typed, but a trailing one
 * survives so `PLATFORM_` can still become `PLATFORM_FEE`.
 */
export function normalizeFeeCodeInput(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9_]+/g, '_')
    .slice(0, MAX_CODE_LENGTH)
}

export function emptyFeeFormValues(): FeeDefinitionFormValues {
  return {
    // Platform-wide by default: a fee that applies everywhere is the common
    // case, and narrowing it to one market is the deliberate choice.
    marketId: null,
    code: '',
    name: '',
    description: '',
    lifecycleStage: FEE_LIFECYCLE_STAGE.ONBOARDING,
    paidByRole: FEE_PARTY_ROLE.PROPERTY_OWNER,
    paidToRole: FEE_PARTY_ROLE.PLATFORM,
    defaultValue: null,
    valueType: FEE_VALUE_TYPE.PERCENTAGE,
    valueBasis: FEE_VALUE_BASIS.PURCHASE_COST,
    unitBasis: null,
    recurrence: FEE_RECURRENCE.ONE_TIME,
    isVatApplicable: false,
    vatPercentage: null,
    isCustomisable: true,
    isActive: true,
    effectiveFrom: null,
    changeReason: '',
  }
}

export function toFeeFormValues(fee: FeeDefinition): FeeDefinitionFormValues {
  return {
    marketId: fee.marketId,
    code: fee.code,
    name: fee.name,
    description: fee.description ?? '',
    lifecycleStage: fee.lifecycleStage,
    paidByRole: fee.paidByRole,
    paidToRole: fee.paidToRole,
    defaultValue: Number(fee.defaultValue),
    valueType: fee.valueType,
    valueBasis: fee.valueBasis,
    unitBasis: fee.unitBasis,
    recurrence: fee.recurrence,
    isVatApplicable: fee.isVatApplicable,
    vatPercentage: fee.vatPercentage === null ? null : Number(fee.vatPercentage),
    isCustomisable: fee.isCustomisable,
    isActive: fee.isActive,
    effectiveFrom: null,
    changeReason: '',
  }
}

/**
 * The basis the form should hold after a value-type switch.
 *
 * A basis the new type also accepts is kept — `investment_value` and
 * `rental_income` are meaningful to a rate and to a flat amount alike, and an
 * admin who picked one should not have it silently replaced. Only a basis the
 * new type cannot use is moved, to that type's most common choice, because the
 * backend rejects the combination otherwise.
 */
export function resolveValueBasisFor(
  values: FeeDefinitionFormValues,
): FeeDefinitionFormValues['valueBasis'] {
  const allowedBases = FEE_VALUE_BASES_BY_VALUE_TYPE[values.valueType]
  if (values.valueBasis !== null && allowedBases.includes(values.valueBasis)) {
    return values.valueBasis
  }
  if (values.valueType === FEE_VALUE_TYPE.FLAT) return FEE_VALUE_BASIS.FLAT_ONLY
  // A rate cannot take `purchase_cost` — it has to name an apportionment, and a
  // running cost recovered from rent is what every rate fee has been so far.
  if (requiresUnitBasis(values.valueType)) return FEE_VALUE_BASIS.RENTAL_INCOME
  return FEE_VALUE_BASIS.PURCHASE_COST
}

/**
 * The area the form should hold after a value-type switch.
 *
 * Only a rate carries one, so switching away from a rate clears it — the API
 * refuses a measurement on a value that is not a rate. Switching *to* a rate
 * defaults to built-up area in sqm: every measurement the platform records is in
 * sqm, and built-up area is what a service charge is levied on.
 */
export function resolveUnitBasisFor(
  values: FeeDefinitionFormValues,
): FeeDefinitionFormValues['unitBasis'] {
  if (!requiresUnitBasis(values.valueType)) return null
  return values.unitBasis ?? FEE_UNIT_BASIS.BUILT_UP_AREA_SQM
}

export function validateFeeForm(
  values: FeeDefinitionFormValues,
  isSupersede: boolean,
  codesInForce: FeeCodeInForce[] = [],
): FeeFormErrors {
  return {
    ...validateFeeIdentity(values, isSupersede, codesInForce),
    ...validateFeeValue(values),
    ...validateFeeAudit(values, isSupersede),
  }
}

function validateFeeIdentity(
  values: FeeDefinitionFormValues,
  isSupersede: boolean,
  codesInForce: FeeCodeInForce[],
): FeeFormErrors {
  const errors: FeeFormErrors = {}
  if (!values.name.trim()) errors.name = 'A name is required.'
  if (isSupersede) return errors

  const code = values.code.trim()
  if (!code) errors.code = 'A code is required.'
  else if (!CODE_PATTERN.test(code)) {
    errors.code = 'Start with a letter; use capitals, digits and underscores only.'
  } else if (code.length > MAX_CODE_LENGTH) {
    errors.code = `Keep the code under ${MAX_CODE_LENGTH} characters.`
  } else if (isFeeCodeInForce(code, values.marketId, codesInForce)) {
    errors.code = 'This code is already in force here. Edit that fee instead, or pick another code.'
  }
  return errors
}

/**
 * Scoped to the market being created for, not global: the same code in a
 * different market is a different fee as far as the API is concerned, and
 * refusing it here would block something the backend would accept.
 */
function isFeeCodeInForce(
  code: string,
  marketId: string | null,
  codesInForce: FeeCodeInForce[],
): boolean {
  return codesInForce.some((inForce) => inForce.code === code && inForce.marketId === marketId)
}

function validateFeeValue(values: FeeDefinitionFormValues): FeeFormErrors {
  const errors: FeeFormErrors = {}
  const isPercentage = values.valueType === FEE_VALUE_TYPE.PERCENTAGE

  if (values.defaultValue === null) errors.defaultValue = 'A value is required.'
  else if (values.defaultValue < 0) errors.defaultValue = 'A fee cannot be negative.'
  else if (isPercentage && values.defaultValue > MAX_PERCENTAGE) {
    errors.defaultValue = 'A rate cannot exceed 100%.'
  }

  const hasUsableBasis =
    values.valueBasis !== null &&
    FEE_VALUE_BASES_BY_VALUE_TYPE[values.valueType].includes(values.valueBasis)
  if (!hasUsableBasis) {
    errors.valueBasis = isPercentage
      ? 'A rate needs something to be a percentage of.'
      : 'Say how the amount is charged — whole, or split by an investor share.'
  }

  if (requiresUnitBasis(values.valueType) && values.unitBasis === null) {
    errors.unitBasis = 'A rate needs the area it is charged against.'
  }

  if (values.isVatApplicable && values.vatPercentage === null) {
    errors.vatPercentage = 'A VAT-applicable fee needs a rate, or it settles at zero VAT.'
  }
  return errors
}

function validateFeeAudit(values: FeeDefinitionFormValues, isSupersede: boolean): FeeFormErrors {
  if (!isSupersede) return {}
  const reason = values.changeReason.trim()
  if (!reason)
    return { changeReason: 'A reason is required — the change is written to the audit trail.' }
  if (reason.length > MAX_CHANGE_REASON_LENGTH) {
    return { changeReason: `Keep the reason under ${MAX_CHANGE_REASON_LENGTH} characters.` }
  }
  return {}
}

export function toCreateFeePayload(values: FeeDefinitionFormValues): CreateFeeDefinitionPayload {
  return {
    // Explicitly null for "All markets" — that is what `fees_master.market_id`
    // stores for a system default every market inherits.
    marketId: values.marketId,
    code: values.code.trim(),
    name: values.name.trim(),
    description: values.description.trim() || null,
    lifecycleStage: values.lifecycleStage,
    defaultValue: values.defaultValue ?? 0,
    valueType: values.valueType,
    valueBasis: resolveValueBasisFor(values),
    unitBasis: resolveUnitBasisFor(values),
    paidByRole: values.paidByRole,
    paidToRole: values.paidToRole,
    recurrence: values.recurrence,
    isVatApplicable: values.isVatApplicable,
    vatPercentage: values.isVatApplicable ? values.vatPercentage : null,
    isCustomisable: values.isCustomisable,
    isActive: values.isActive,
  }
}

export function toSupersedeFeePayload(
  values: FeeDefinitionFormValues,
): SupersedeFeeDefinitionPayload {
  return {
    changeReason: values.changeReason.trim(),
    ...(values.effectiveFrom && { effectiveFrom: dateToIso(values.effectiveFrom) }),
    name: values.name.trim(),
    description: values.description.trim() || null,
    defaultValue: values.defaultValue ?? 0,
    valueType: values.valueType,
    valueBasis: resolveValueBasisFor(values),
    unitBasis: resolveUnitBasisFor(values),
    recurrence: values.recurrence,
    isVatApplicable: values.isVatApplicable,
    vatPercentage: values.isVatApplicable ? values.vatPercentage : null,
    isCustomisable: values.isCustomisable,
    isActive: values.isActive,
  }
}
